import React, { useMemo } from 'react';
import { applyModel } from '../../helpers';
import { updateModelItem } from '../../store/actions';
import { ModelCard } from './Card';
import { translate } from '@shakkerai/util-i18n';
import DivideLine from './DivideLine';
import { useTempState, useUnitData } from '@starflow/app/store';

const colsClassNames = {
  4: 'grid-cols-4',
  5: 'grid-cols-5',
};

export const BestModelList = React.memo(
  ({ data, cols = 5, cardSize = {}, query, withHotBadge, showTag, withAuthorInfo, from }) => {
    const { items = [], loading } = data;
    const { checkpoint: unitCheckpoint, loraModels: unitLoraModels = [] } = useUnitData();

    const tempPickingModel = useTempState('tempPickingModel');
    const tempCheckpoint = useTempState('tempCheckpoint');
    const tempLoras = useTempState('tempLoras', []);

    const checkpoint = tempPickingModel ? tempCheckpoint : unitCheckpoint;
    const loraModels = tempPickingModel ? tempLoras : unitLoraModels;

    const currentCheckpointId = checkpoint?.versionId;
    const currentLoraIdsMap = Object.fromEntries(loraModels?.map((item) => [item?.versionId, true]));

    const emptyText = useMemo(() => {
      if (from === 'favorite') {
        return translate('model_nofav');
      }
      if (from === 'history') {
        return translate('model_nohistory');
      }
      return translate('model_nosearch');
    }, [from]);

    const cards = useMemo(() => {
      const all = [...items];
      const bestModels = [];
      const normalModels = [];
      all.forEach((al) => {
        al.isSelected ? bestModels.push(al) : normalModels.push(al);
      });
      return { normalModels, bestModels };
    }, [items]);

    if (!items?.length) {
      if (loading) {
        return (
          <div className="flex items-center justify-center mt-32 text-xs text-[#7f7f7f]">{translate('loading')}...</div>
        );
      }

      return <div className="flex items-center justify-center mt-32 text-xs text-[#7f7f7f]">{emptyText}</div>;
    }
    const { bestModels, normalModels } = cards;
    return (
      <>
        {bestModels.length ? (
          <div className={`grid ${colsClassNames[cols] || colsClassNames['5']} gap-4 mt-3 pb-4`}>
            {bestModels.map((item, index) => {
              const using = !!currentLoraIdsMap[item?.versionId] || currentCheckpointId === item?.versionId;

              return (
                <ModelCard
                  data={item}
                  using={using}
                  key={item?.versionId}
                  width={cardSize.width}
                  height={cardSize.height}
                  showTag={showTag}
                  query={query}
                  showAuthorInfo={withAuthorInfo}
                  hotBadge={index < 3 && withHotBadge ? index + 1 : null}
                  onClick={applyModel}
                  onUpdate={updateModelItem}
                />
              );
            })}
          </div>
        ) : null}
        {normalModels.length ? (
          <>
            {bestModels.length > 1 ? <DivideLine /> : null}
            <div className={`grid ${colsClassNames[cols] || colsClassNames['5']} gap-4 mt-3 pb-4`}>
              {normalModels.map((item) => {
                const using = !!currentLoraIdsMap[item?.versionId] || currentCheckpointId === item?.versionId;
                return (
                  <ModelCard
                    data={item}
                    using={using}
                    key={item?.versionId}
                    width={cardSize.width}
                    height={cardSize.height}
                    showTag={showTag}
                    query={query}
                    showAuthorInfo={withAuthorInfo}
                    hotBadge={null}
                    onClick={applyModel}
                    onUpdate={updateModelItem}
                  />
                );
              })}
            </div>
          </>
        ) : null}
        {loading ? (
          <div className="flex self-stretch items-center justify-center mb-4 text-xs text-[#7f7f7f]">
            {translate('loading')}...
          </div>
        ) : null}
      </>
    );
  },
);
